/**
 * Supabase Storage helpers for landing page V2 component files
 * Uses the `event-landing-pages` bucket (separate from V1's `landing-pages` bucket)
 */

import { uploadFileToStorage, deleteFilesFromStorage } from "@/lib/database/client/storage";

/** Exported for the duplication flow (`duplicate-shared.ts`), which copies a page's images into
 * a new page's own storage folder rather than leaving a duplicate pointing at the source's files. */
export const BUCKET = "event-landing-pages";

/**
 * Builds an opaque storage filename that keeps only the file extension.
 *
 * The original `file.name` is intentionally discarded: a human-readable name
 * (e.g. a venue name baked into a hero image) would otherwise appear verbatim in
 * the public storage URL and leak a page's secret location to anyone who curls
 * the page — even when the page body is gated behind auth/password. The extension
 * is preserved (lowercased) so OG/video format detection by URL still works.
 * @param file - The file being uploaded.
 * @returns A random filename of the form `<uuid>.<ext>` (extension omitted when absent).
 */
function buildOpaqueFileName(file: File): string {
  const match = /\.([a-zA-Z0-9]+)$/.exec(file.name);
  const ext = match ? `.${match[1].toLowerCase()}` : "";
  return `${crypto.randomUUID()}${ext}`;
}

/**
 * Single upload boundary for this feature's bucket. SVG uploads are sanitized here — at the
 * point every `upload*` helper funnels through — so no caller can store an SVG carrying
 * script (pen-test Finding 3 stored-XSS), including paths that don't pre-process via
 * `compressImage`. Non-SVG files pass through unchanged.
 * @param path - Destination storage path.
 * @param file - The file to upload.
 * @returns The public URL of the uploaded file.
 * @throws {Error} When an SVG cannot be sanitized.
 */
async function uploadToBucket(path: string, file: File): Promise<string> {
  let safe: File | null = file;
  if (file.type === "image/svg+xml") {
    // Lazy-load the SVG sanitizer so the public landing-page server bundle never statically pulls
    // isomorphic-dompurify → jsdom: a CJS `require()` of an ESM dep in that chain throws
    // ERR_REQUIRE_ESM on Vercel and 500s the page on cold start (DEV-3674). This module is reachable
    // from the public render (builder-store → storage), but uploads only run from the builder, so
    // deferring the import keeps SVG sanitization fully intact while keeping jsdom out of the SSR path.
    const { sanitizeSvgFile } = await import("@/lib/utils/svg-sanitization");
    safe = await sanitizeSvgFile(file);
  }
  if (!safe) {
    throw new Error("SVG could not be sanitized");
  }
  return uploadFileToStorage(BUCKET, path, safe);
}

/**
 * Uploads a landing page component file to Supabase Storage
 * @param pageId - The landing page ID
 * @param componentId - The component ID that owns this file
 * @param file - The file to upload
 * @returns The public URL of the uploaded file
 */
export async function uploadComponentFile(pageId: number, componentId: string, file: File): Promise<string> {
  const path = `${pageId}/components/${componentId}/${buildOpaqueFileName(file)}`;
  return uploadToBucket(path, file);
}

/**
 * Deletes a landing page component file from Supabase Storage
 * Extracts the storage path from a public URL. No-ops for non-storage URLs (e.g. base64).
 * @param publicUrl - The public URL returned by uploadComponentFile
 */
export async function deleteComponentFile(publicUrl: string): Promise<void> {
  const path = extractPathFromUrl(publicUrl);
  if (path) {
    await deleteFilesFromStorage(BUCKET, [path]);
  }
}

/**
 * Uploads an activity image to Supabase Storage
 * @param pageId - The landing page ID (used as folder prefix)
 * @param file - The image file to upload
 * @returns The public URL of the uploaded image
 */
export async function uploadActivityImage(pageId: number, file: File): Promise<string> {
  const path = `${pageId}/activities/${buildOpaqueFileName(file)}`;
  return uploadToBucket(path, file);
}

/**
 * Deletes an activity image from Supabase Storage
 * No-ops for non-storage URLs (e.g. external URLs).
 * @param publicUrl - The public URL returned by uploadActivityImage
 */
export async function deleteActivityImage(publicUrl: string): Promise<void> {
  const path = extractPathFromUrl(publicUrl);
  if (path) {
    await deleteFilesFromStorage(BUCKET, [path]);
  }
}

/**
 * Uploads a team member image to Supabase Storage
 * @param pageId - The landing page ID (used as folder prefix)
 * @param file - The image file to upload
 * @returns The public URL of the uploaded image
 */
export async function uploadTeamMemberImage(pageId: number, file: File): Promise<string> {
  const path = `${pageId}/team/${buildOpaqueFileName(file)}`;
  return uploadToBucket(path, file);
}

/**
 * Deletes a team member image from Supabase Storage
 * No-ops for non-storage URLs (e.g. external URLs).
 * @param publicUrl - The public URL returned by uploadTeamMemberImage
 */
export async function deleteTeamMemberImage(publicUrl: string): Promise<void> {
  const path = extractPathFromUrl(publicUrl);
  if (path) {
    await deleteFilesFromStorage(BUCKET, [path]);
  }
}

/**
 * Uploads a sponsor logo image to Supabase Storage
 * @param pageId - The landing page ID (used as the storage path prefix)
 * @param file - The image file to upload
 * @returns The public URL of the uploaded image
 */
export async function uploadSponsorLogo(pageId: number, file: File): Promise<string> {
  const path = `${pageId}/sponsors/${buildOpaqueFileName(file)}`;
  return uploadToBucket(path, file);
}

/**
 * Deletes a sponsor logo from Supabase Storage.
 * No-ops for non-storage URLs (e.g. external URLs).
 * @param publicUrl - The public URL returned by uploadSponsorLogo
 */
export async function deleteSponsorLogo(publicUrl: string): Promise<void> {
  const path = extractPathFromUrl(publicUrl);
  if (path) {
    await deleteFilesFromStorage(BUCKET, [path]);
  }
}

/**
 * Uploads an event's email header image to Supabase Storage. Event-scoped (not page-scoped)
 * since it's shared by all of an event's activity email templates plus the calendar-invite
 * template, which has no landing-page context.
 * @param eventId - The event id (used as folder prefix)
 * @param file - The image file to upload
 * @returns The public URL of the uploaded image
 */
export async function uploadEmailHeaderImage(eventId: number, file: File): Promise<string> {
  const path = `${eventId}/email-header/${buildOpaqueFileName(file)}`;
  return uploadToBucket(path, file);
}

/**
 * Deletes an email header image from Supabase Storage.
 * No-ops for non-storage URLs (e.g. external URLs).
 * @param publicUrl - The public URL returned by uploadEmailHeaderImage
 */
export async function deleteEmailHeaderImage(publicUrl: string): Promise<void> {
  const path = extractPathFromUrl(publicUrl);
  if (path) {
    await deleteFilesFromStorage(BUCKET, [path]);
  }
}

/**
 * Checks if a URL is a Supabase storage URL for this bucket
 * @param url - The URL to check
 * @returns True if the URL points to our storage bucket
 */
export function isStorageUrl(url: string): boolean {
  return extractPathFromUrl(url) !== null;
}

/**
 * Extracts the storage path from a Supabase public URL
 * URL format: .../storage/v1/object/public/event-landing-pages/{path}
 * Exported for the duplication flow, which needs the raw path to copy an image to a new location.
 * @returns The storage path, or null if the URL doesn't match
 */
export function extractPathFromUrl(url: string): string | null {
  const marker = `/storage/v1/object/public/${BUCKET}/`;
  const index = url.indexOf(marker);
  if (index === -1) return null;
  return url.substring(index + marker.length);
}
